import classNames from "classnames";
import { useRouter } from "next/navigation";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import Button from "rivals/components/shared/Button";
import BillingForm from "rivals/components/shared/Form/BillingInformation/container";
import PayPalCheckout from "rivals/components/shared/PayPalCheckout";
import { AlertTypes } from "rivals/redux/alert/reducer";
import { initialState, mergeAddress } from "rivals/services/billing/types";
import useObick from "rivals/services/billing/useObick";
import {
  CARD_TYPES,
  CENTS_PER_DOLLAR,
  CUSTOMER_SUPPORT,
  PATHS,
  PAY_OUTSTANDING_BALANCE,
  PAYPAL_SUBSCRIPTION
} from "rivals/shared/constants";
import { formatCurrency } from "rivals/shared/utils/money";
import { setData } from "rivals/shared/utils/qa";
import styles from "./BillingInformation.module.scss";
import strings from "./strings";
import { Props } from "./types";
import BillingHistory from "../BillingHistory/container";

const BillingInformation: FunctionComponent<Props> = ({
  address,
  className,
  billingInformation,
  message,
  payPalAddress,
  payPalEmail,
  payPalToken,
  payOutstandingBalance,
  railsConstants,
  showAlert,
  updateBillingInformation,
  updatePayPalAddress,
  updatePayPalAuthorizationData,
  userEmail = "",
  userPayPalEmail,
  submitting
}) => {
  const router = useRouter();

  if (!railsConstants) {
    return null;
  }

  const { autoRenewalOptInStates, obickSourceUrl } = railsConstants;

  const containerClasses = classNames(styles.billingInformation, className);

  const {
    addBillingInfo,
    creditCardNumber,
    editBillingInfo,
    expirationDate,
    header,
    noBillingInformation,
    outstandingBalanceMsg,
    paymentMethod,
    payPal,
    payPalAccount,
    postalCode
  } = strings;

  const [editBillingOpen, openEditBilling] = useState(false);

  let initialFormState = initialState;

  if (address) {
    initialFormState = mergeAddress(address);
  }

  const {
    billingFormProps,
    submit,
    isCheckingOut,
    debitAgreedState,
    valid
  } = useObick({
    autoRenewalOptInStates,
    formReady: editBillingOpen && !payPalToken,
    initialState: initialFormState,
    obickSourceUrl,
    payPalAddress,
    payPalEmail,
    payPalToken,
    updateBillingAction: updateBillingInformation
  });

  const prevSubmittingRef = useRef<boolean>(false);

  useEffect(() => {
    if (prevSubmittingRef.current && !submitting) {
      openEditBilling(false);
    }
  });

  useEffect(() => {
    prevSubmittingRef.current = submitting;
  });

  const noBillingInfo = (
    <div className={styles.section}>
      <label className={styles.noBillingInfo}>{noBillingInformation}</label>
    </div>
  );

  let billingInformationSummary = noBillingInfo;
  let showBillingInfo = false;
  let outstandingBalanceSection = null;
  let openBillingText = addBillingInfo;

  useEffect(() => {
    if (!billingInformation?.outstandingBalance && message) {
      showAlert({
        message: message,
        persisted: true,
        type: AlertTypes.SUCCESS
      });
      router.push(PATHS.NEW_SUBSCRIPTION);
    }
  }, [billingInformation?.outstandingBalance, message]);

  if (billingInformation) {
    const {
      cardType,
      obfuscatedCreditCard,
      expirationDate: billingExpirationDate,
      outstandingBalance,
      postalCode: billingPostalCode
    } = billingInformation;

    const isPayPalSubscription = cardType === PAYPAL_SUBSCRIPTION;

    showBillingInfo =
      isPayPalSubscription ||
      (!!obfuscatedCreditCard && !!billingExpirationDate && !!postalCode);

    if (outstandingBalance) {
      const { cents, currency } = outstandingBalance;
      outstandingBalanceSection = (
        <div className={styles.outstandingBalanceContainer}>
          <span className={styles.outstandingBalance}>
            <span>{`${outstandingBalanceMsg} `}</span>
            <span className={styles.outstandingBalanceValue}>
              {formatCurrency(cents / CENTS_PER_DOLLAR, currency)}
            </span>
          </span>
          <div className={styles.buttonGroup}>
            <Button
              buttonText={PAY_OUTSTANDING_BALANCE}
              className={styles.button}
              disabled={submitting}
              onClick={payOutstandingBalance}
            />
            <Button
              buttonText={CUSTOMER_SUPPORT}
              className={styles.button}
              disabled={submitting}
              inverted
              url={PATHS.HELP}
            />
          </div>
        </div>
      );
    }

    const payPalSection = (
      <div className={styles.infoWrapper}>
        <div className={styles.section}>
          <div className={styles.payPalCheckout}>
            <PayPalCheckout readOnly />
          </div>
        </div>
        <div className={styles.section}>
          <label className={styles.sectionHeader}>{payPalAccount}</label>
          <div {...setData("billing-info", "user-paypal-email")}>
            {userPayPalEmail}
          </div>
        </div>
      </div>
    );

    const creditCardSection = (
      <div className={styles.infoWrapper}>
        <div className={styles.section}>
          <div className={styles.creditCards}>
            {CARD_TYPES.map((card, idx) => (
              <img
                alt={`${card} icon`}
                key={idx}
                src={`/static/images/credit_cards/${card}.png`}
                {...setData("edit-billing-info", `${card}-card-image`)}
              />
            ))}
          </div>
        </div>
        <div className={styles.section}>
          <div className={styles.sectionHeaderWrapper}>
            <label
              className={styles.sectionHeader}
              {...setData("billing-info", "ccn-header")}
            >
              {creditCardNumber}
            </label>
          </div>
          <div {...setData("billing-info", "masked-ccn")}>
            {obfuscatedCreditCard}
          </div>
        </div>
        <div className={styles.section}>
          <div className={styles.sectionHeaderWrapper}>
            <label
              className={styles.sectionHeader}
              {...setData("billing-info", "exp-date-header")}
            >
              {expirationDate}
            </label>
          </div>
          <div {...setData("billing-info", "exp-date")}>
            {billingExpirationDate}
          </div>
        </div>
        <div className={styles.section}>
          <div className={styles.sectionHeaderWrapper}>
            <label
              className={styles.sectionHeader}
              {...setData("billing-info", "postal-code-header")}
            >
              {postalCode}
            </label>
          </div>
          <div {...setData("billing-info", "postal-code")}>
            {billingPostalCode}
          </div>
        </div>
      </div>
    );

    if (showBillingInfo) {
      openBillingText = editBillingInfo;
      billingInformationSummary = isPayPalSubscription
        ? payPalSection
        : creditCardSection;
    }
  }

  const billingInformationDisplay = (
    <div className={styles.wrapper}>
      {billingInformationSummary}
      <div className={styles.editButtonContainer}>
        <Button
          buttonText={openBillingText}
          data="billing-info-edit-billing-info"
          inverted
          onClick={(): void => {
            openEditBilling(true);
          }}
          size="small"
        />
      </div>
      {outstandingBalanceSection}
    </div>
  );

  return (
    <div className={containerClasses}>
      <h1 className={styles.header} {...setData("billing-info", "header")}>
        {header}
      </h1>
      <div className={styles.subheader}>{paymentMethod}</div>
      {editBillingOpen ? (
        <BillingForm
          {...billingFormProps}
          closeForm={(): void => {
            openEditBilling(false);
            updatePayPalAddress();
            updatePayPalAuthorizationData();
          }}
          debitAgreedState={debitAgreedState}
          isCheckingOut={isCheckingOut}
          payPalTitle={payPal}
          submit={submit}
          userEmail={userEmail}
          valid={valid}
        />
      ) : (
        billingInformationDisplay
      )}
      <BillingHistory />
    </div>
  );
};

export default BillingInformation;
